import type { Presence } from '@/components/core/presence-avatar/presence-avatar';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import {
  breakTypeOf,
  findOpenBreak,
  formatTimeOfDay,
  isOpenRecord,
  sessionStartIso,
  visibleBreaks,
} from '@/modules/time-attendance/attendance/services/attendance-rules';
import type { AttendanceRecord } from '@/modules/time-attendance/attendance/types/attendance.types';

/** A break this long shows as "On break, 2 h+" with a dashed ring (prototype presence rule). */
export const LONG_BREAK_MS = 2 * 60 * 60 * 1000;

/** A translation key with its values. */
export interface PresenceText {
  key: string;
  values?: Record<string, string>;
}

export type EmployeePresence = Exclude<Presence, 'off'>;

export interface PresenceView {
  presence: EmployeePresence;
  label: PresenceText;
  detail: PresenceText;
}

export interface PresenceInput {
  /** The person's attendance record for the organization's today, if any. */
  record?: AttendanceRecord | null;
  /** Approved leave covering today: its last day (date only). */
  leaveToday?: { lastDay: string } | null;
  /** Today is one of the person's work days (current HR term). */
  isWorkDay: boolean;
  /** Today is a holiday for the person; its name when known. */
  isHoliday: boolean;
  holidayName?: string;
  now: Date;
  /**
   * Today's shift start (UTC). Not stored yet: per-employee shift times come with the future shift
   * configuration. TODO(shift-config): pass the person's shift start for today once shifts exist; until
   * then "Shift not started" never shows and a work day without a clock-in reads "Not clocked in".
   */
  shiftStartUtc?: string;
  /** Zone for times without a record zone (the organization's). */
  timeZone: string;
}

/** The order of the prototype's status key (`PRESENCE_ORDER`), used to sort and to draw the key. */
export const PRESENCE_ORDER: EmployeePresence[] = [
  'in',
  'late',
  'notstarted',
  'leave',
  'offsched',
  'break',
  'breaklong',
  'out',
  'dayoff',
];

export const PRESENCE_LABEL_KEY: Record<EmployeePresence, string> = {
  in: 'STATUS_CLOCKED_IN',
  late: 'STATUS_NOT_CLOCKED_IN',
  notstarted: 'STATUS_SHIFT_NOT_STARTED',
  leave: 'STATUS_ON_LEAVE',
  offsched: 'STATUS_CLOCKED_IN_OFF_SCHEDULE',
  break: 'STATUS_ON_BREAK',
  breaklong: 'STATUS_ON_BREAK_LONG',
  out: 'STATUS_CLOCKED_OUT',
  dayoff: 'STATUS_OFF_TODAY',
};

/**
 * A person's presence today (prototype `presenceOf`), from their attendance record, leave, work days,
 * holiday and (once shift times exist) shift start:
 * - clocked in: on a break (2 h+ is "long"), off-schedule on a day off / holiday / leave day, else in;
 * - clocked out for the day: out;
 * - no clock-in: on leave, off today (not a work day or a holiday), shift not started (before the shift
 *   start, only when it is known), else not clocked in.
 */
export const derivePresence = ({
  record,
  leaveToday,
  isWorkDay,
  isHoliday,
  holidayName,
  now,
  shiftStartUtc,
  timeZone,
}: PresenceInput): PresenceView => {
  const zone = record?.Timezone || timeZone;
  const time = (iso?: string) => (iso ? formatTimeOfDay(iso, false, zone) : '');
  const view = (presence: EmployeePresence, detail: PresenceText): PresenceView => ({
    presence,
    label: { key: PRESENCE_LABEL_KEY[presence] },
    detail,
  });

  if (record?.StartTimeUtc && isOpenRecord(record)) {
    const openBreak = findOpenBreak(visibleBreaks(record.BreakTimeDetails ?? []));
    if (openBreak) {
      const isLunch = breakTypeOf(openBreak) === 'Lunch';
      const isLong = now.getTime() - new Date(openBreak.StartTimeUtc).getTime() >= LONG_BREAK_MS;
      const result = view(isLong ? 'breaklong' : 'break', {
        key: isLunch ? 'PRESENCE_DETAIL_LUNCH_SINCE' : 'PRESENCE_DETAIL_BREAK_SINCE',
        values: { time: time(openBreak.StartTimeUtc) },
      });
      if (isLunch && !isLong) result.label = { key: 'STATUS_ON_LUNCH_BREAK' };
      return result;
    }
    const clockedIn = time(sessionStartIso(record));
    if (leaveToday || isHoliday || !isWorkDay) {
      const reason = leaveToday
        ? 'STATUS_ON_LEAVE'
        : isHoliday
          ? 'PRESENCE_REASON_HOLIDAY'
          : 'PRESENCE_REASON_DAY_OFF';
      return view('offsched', {
        key: 'PRESENCE_DETAIL_OFF_SCHEDULE',
        values: { reason, time: clockedIn },
      });
    }
    return view('in', { key: 'PRESENCE_DETAIL_CLOCKED_IN', values: { time: clockedIn } });
  }

  if (record?.StartTimeUtc) {
    return view('out', {
      key: 'PRESENCE_DETAIL_CLOCKED_OUT',
      values: { time: time(record.EndTimeUtc) },
    });
  }

  if (leaveToday) {
    return view('leave', {
      key: 'PRESENCE_DETAIL_UNTIL',
      values: { date: formatDateOnly(leaveToday.lastDay) },
    });
  }
  if (isHoliday) {
    return view(
      'dayoff',
      holidayName
        ? { key: 'PRESENCE_DETAIL_HOLIDAY', values: { name: holidayName } }
        : { key: 'PRESENCE_REASON_HOLIDAY' }
    );
  }
  if (!isWorkDay) return view('dayoff', { key: 'PRESENCE_DETAIL_NOT_WORK_DAY' });

  // TODO(shift-config): with per-employee shift times, before the shift starts it is not started yet.
  if (shiftStartUtc && now.getTime() < new Date(shiftStartUtc).getTime()) {
    return view('notstarted', {
      key: 'PRESENCE_DETAIL_SHIFT_STARTS',
      values: { time: time(shiftStartUtc) },
    });
  }
  return view(
    'late',
    shiftStartUtc
      ? { key: 'PRESENCE_DETAIL_SHIFT_STARTED', values: { time: time(shiftStartUtc) } }
      : { key: 'PRESENCE_DETAIL_NO_CLOCK_IN' }
  );
};

/** Translates a presence text; a value that is itself a key (the off-schedule reason) is translated too. */
export const presenceText = (
  t: (key: string, values?: Record<string, string>) => string,
  text: PresenceText
): string =>
  t(
    text.key,
    text.values &&
      Object.fromEntries(
        Object.entries(text.values).map(([name, value]) => [
          name,
          name === 'reason' ? t(value) : value,
        ])
      )
  );
